import {
  ICON_LABELS,
  ICONS,
  ITEM_TYPE_LABELS,
  ITEM_TYPES,
  LIMITS,
  STATUS_LABELS,
  STATUSES,
  type ItemIcon,
  type ItemStatus,
  type ItemType,
  type NodeCore,
  type Placement,
  type ProjectLabels,
} from '@blueprint/domain'
import { useEffect, useId, useState } from 'react'
import { RelationshipList } from '../features/context/ContextEditor.js'
import { ReferencesEditor } from '../features/cards/intent/ReferencesEditor.js'
import { getRepository } from '../state/board-store.js'
import { Field, Select, TextInput } from './controls'
import styles from './controls.module.css'

/**
 * The fields every item shares — story map cards, mindmap nodes and workflow nodes — so the
 * three are edited alike: type, status, owner, icon, tags and placement, references and
 * relationships. Tags and placement take a new label or one already used in the project.
 */

const NO_LABELS: ProjectLabels = { tags: [], groups: [], columns: [], swimlanes: [], rows: [] }

/** The project's labels, read once per open. A failed read only means no suggestions. */
export function useProjectLabels(boardId: string | null | undefined): ProjectLabels {
  const [labels, setLabels] = useState<ProjectLabels>(NO_LABELS)
  useEffect(() => {
    if (!boardId) return
    let cancelled = false
    getRepository()
      .projectLabels(boardId)
      .then((next) => !cancelled && setLabels(next))
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [boardId])
  return labels
}

// --- icons ---------------------------------------------------------------------------

/**
 * An icon as SVG path data on a 24×24 grid, drawn on the mindmap canvas and in the page
 * alike. `fill` is a solid shape in `colour`, with `stroke` drawn over it in white; without
 * `fill`, `stroke` is the outline, drawn in `colour` or, when that is null, the item's.
 */
export interface IconGlyph {
  fill?: string
  stroke: string
  colour: string | null
}

const CIRCLE = 'M12 2a10 10 0 1 0 0 20a10 10 0 1 0 0-20Z'

export const ICON_GLYPHS: Record<ItemIcon, IconGlyph> = {
  danger: { fill: CIRCLE, stroke: 'M8.5 8.5l7 7M15.5 8.5l-7 7', colour: '#e74c3c' },
  warning: { fill: 'M12 2.5L22.5 20.5H1.5Z', stroke: 'M12 9v5M12 17v.01', colour: '#f39c12' },
  ok: { fill: CIRCLE, stroke: 'M7.5 12.5l3 3 6-6.5', colour: '#2ecc71' },
  home: { stroke: 'M3 11l9-8 9 8M5 9.5V21h5v-6h4v6h5V9.5', colour: null },
  trash: { stroke: 'M3 6h18M8 6V4h8v2M5.5 6l1 15h11l1-15M10 11v6M14 11v6', colour: null },
  form: { stroke: 'M8 4H6v18h12V4h-2M8 2h8v4H8ZM9 11h6M9 15h6', colour: null },
  user: { stroke: 'M12 3a4 4 0 1 0 0 8a4 4 0 1 0 0-8ZM4 21c0-4 4-7 8-7s8 3 8 7', colour: null },
  lock: { stroke: 'M5 11h14v10H5ZM8 11V7a4 4 0 0 1 8 0v4', colour: null },
  star: { stroke: 'M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6-4.9-4.6 6.6-.8Z', colour: null },
  flag: { stroke: 'M5 22V3M5 4h13l-3 4.5 3 4.5H5', colour: null },
  bug: {
    stroke: 'M8 8a4 4 0 0 1 8 0M7 9h10v6a5 5 0 0 1-10 0ZM12 9v11M3 13h4M17 13h4M4 19l3.5-2M20 19l-3.5-2M4 7l3 2M20 7l-3 2',
    colour: null,
  },
  idea: {
    stroke: 'M9 18h6M10 21.5h4M12 2.5a6.5 6.5 0 0 0-4 11.6c.7.6 1 1.4 1 2.4v1h6v-1c0-1 .3-1.8 1-2.4a6.5 6.5 0 0 0-4-11.6Z',
    colour: null,
  },
  question: { stroke: `${CIRCLE}M9.2 9a3 3 0 0 1 5.8 1c0 2-3 2.5-3 4.5M12 17.5v.01`, colour: null },
  calendar: { stroke: 'M3 5h18v16H3ZM3 10h18M8 2.5v5M16 2.5v5', colour: null },
  mail: { stroke: 'M2.5 5h19v14h-19ZM2.5 6l9.5 7 9.5-7', colour: null },
  database: {
    stroke: 'M4 5.5c0-1.9 3.6-3 8-3s8 1.1 8 3-3.6 3-8 3-8-1.1-8-3ZM4 5.5v13c0 1.9 3.6 3 8 3s8-1.1 8-3v-13M4 12c0 1.9 3.6 3 8 3s8-1.1 8-3',
    colour: null,
  },
  search: { stroke: 'M10.5 3a7.5 7.5 0 1 0 0 15a7.5 7.5 0 1 0 0-15ZM16 16l5.5 5.5', colour: null },
  clock: { stroke: `${CIRCLE}M12 6.5V12l3.5 2.5`, colour: null },
  link: {
    stroke: 'M10 14a4.5 4.5 0 0 0 6.4 0l3.3-3.3a4.5 4.5 0 0 0-6.4-6.4L12 5.6M14 10a4.5 4.5 0 0 0-6.4 0l-3.3 3.3a4.5 4.5 0 0 0 6.4 6.4L12 18.4',
    colour: null,
  },
}

/** One icon, as an inline SVG. Decorative: say what it is in text next to it. */
export function IconSvg({ icon, size = 20 }: { icon: ItemIcon; size?: number }) {
  const glyph = ICON_GLYPHS[icon]
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {glyph.fill ? <path d={glyph.fill} fill={glyph.colour ?? 'currentColor'} /> : null}
      <path d={glyph.stroke} stroke={glyph.fill ? '#fff' : (glyph.colour ?? 'currentColor')} />
    </svg>
  )
}

/** The icon library as a grid of buttons, with None first. */
export function IconPicker({
  value,
  onPick,
  label = 'Icon',
  disabled = false,
}: {
  value: ItemIcon | null
  onPick: (icon: ItemIcon | null) => void
  label?: string
  disabled?: boolean
}) {
  return (
    <div className={styles.iconGrid} role="radiogroup" aria-label={label}>
      <button type="button" role="radio" aria-checked={value === null} aria-label="No icon" title="No icon" className={styles.iconOption} disabled={disabled} onClick={() => onPick(null)}>
        <span className={styles.iconNone} />
      </button>
      {ICONS.map((icon) => (
        <button
          key={icon}
          type="button"
          role="radio"
          aria-checked={value === icon}
          aria-label={ICON_LABELS[icon]}
          title={ICON_LABELS[icon]}
          className={styles.iconOption}
          disabled={disabled}
          onClick={() => onPick(icon)}
        >
          <IconSvg icon={icon} />
        </button>
      ))}
    </div>
  )
}

// --- type, status --------------------------------------------------------------------

export function TypeSelect({ id, value, onChange, disabled }: { id?: string; value: ItemType | null; onChange: (type: ItemType | null) => void; disabled?: boolean }) {
  return (
    <Select id={id} value={value ?? ''} disabled={disabled} onChange={(event) => onChange((event.target.value || null) as ItemType | null)}>
      <option value="">Not set</option>
      {ITEM_TYPES.map((type) => (
        <option key={type} value={type}>
          {ITEM_TYPE_LABELS[type]}
        </option>
      ))}
    </Select>
  )
}

/** A card always has a status; a node may have none, which `allowNone` offers. */
export function StatusSelect({
  id,
  value,
  onChange,
  allowNone = false,
  disabled,
}: {
  id?: string
  value: ItemStatus | null
  onChange: (status: ItemStatus | null) => void
  allowNone?: boolean
  disabled?: boolean
}) {
  return (
    <Select id={id} value={value ?? ''} disabled={disabled} onChange={(event) => onChange((event.target.value || null) as ItemStatus | null)}>
      {allowNone ? <option value="">Not set</option> : null}
      {STATUSES.map((status) => (
        <option key={status} value={status}>
          {STATUS_LABELS[status]}
        </option>
      ))}
    </Select>
  )
}

// --- tags and placement --------------------------------------------------------------

const sameLabel = (a: string, b: string) => a.toLowerCase() === b.toLowerCase()

/** The tags as removable chips, and an input that adds one: typed, or picked from the suggestions. */
export function TagInput({
  id,
  value,
  suggestions,
  onChange,
  disabled = false,
}: {
  id?: string
  value: readonly string[]
  suggestions: readonly string[]
  onChange: (tags: string[]) => void
  disabled?: boolean
}) {
  const [draft, setDraft] = useState('')
  const listId = useId()
  const full = value.length >= LIMITS.tagCount

  const add = (raw: string) => {
    const tag = raw.trim()
    setDraft('')
    if (!tag || full || value.some((existing) => sameLabel(existing, tag))) return
    onChange([...value, tag])
  }

  return (
    <div className={styles.tags}>
      {value.map((tag) => (
        <span key={tag} className={styles.tag}>
          {tag}
          {disabled ? null : (
            <button type="button" className={styles.tagRemove} aria-label={`Remove tag ${tag}`} onClick={() => onChange(value.filter((existing) => existing !== tag))}>
              ×
            </button>
          )}
        </span>
      ))}
      {disabled ? null : (
        <>
          <TextInput
            id={id}
            className={styles.tagInput}
            list={listId}
            value={draft}
            maxLength={LIMITS.tag}
            disabled={full}
            placeholder={full ? `At most ${LIMITS.tagCount} tags` : 'Add a tag…'}
            onChange={(event) => {
              const next = event.target.value
              // Picking from the list is not typing: it arrives whole, so it is added at once.
              const picked = !(event.nativeEvent instanceof InputEvent) || event.nativeEvent.inputType === 'insertReplacementText'
              if (picked && suggestions.some((suggestion) => suggestion === next)) add(next)
              else setDraft(next)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ',') {
                event.preventDefault()
                add(draft)
              } else if (event.key === 'Backspace' && draft === '' && value.length > 0) {
                onChange(value.slice(0, -1))
              }
            }}
            onBlur={() => add(draft)}
          />
          <datalist id={listId}>
            {suggestions
              .filter((suggestion) => !value.some((existing) => sameLabel(existing, suggestion)))
              .map((suggestion) => (
                <option key={suggestion} value={suggestion} />
              ))}
          </datalist>
        </>
      )}
    </div>
  )
}

const PLACEMENT_FIELDS = [
  { key: 'group', label: 'Column group', list: 'groups' },
  { key: 'column', label: 'Column', list: 'columns' },
  { key: 'swimlane', label: 'Swimlane', list: 'swimlanes' },
  { key: 'row', label: 'Row', list: 'rows' },
] as const

/** Where the item lands on a story map, as titles; empty is not set. */
export function PlacementFields({
  value,
  labels,
  onChange,
  disabled = false,
}: {
  value: Placement
  labels: ProjectLabels
  onChange: (placement: Placement) => void
  disabled?: boolean
}) {
  return (
    <>
      {PLACEMENT_FIELDS.map((field) => (
        <Field key={field.key} label={field.label}>
          {(id) => (
            <>
              <TextInput
                id={id}
                list={`${id}-options`}
                value={value[field.key]}
                maxLength={LIMITS.title}
                disabled={disabled}
                onChange={(event) => onChange({ ...value, [field.key]: event.target.value })}
              />
              <datalist id={`${id}-options`}>
                {labels[field.list].map((option) => (
                  <option key={option} value={option} />
                ))}
              </datalist>
            </>
          )}
        </Field>
      ))}
    </>
  )
}

// --- a node's shared fields ----------------------------------------------------------

/**
 * Everything a mindmap or workflow node shares with a card, beyond its title and
 * description: type, status, owner, icon, tags, placement, references and relationships.
 * `targets` are the other nodes of the same board, for relationships.
 */
export function NodeCoreFields({
  node,
  labels,
  targets,
  readOnly,
  onChange,
}: {
  node: NodeCore
  labels: ProjectLabels
  targets: readonly { id: string; label: string }[]
  readOnly: boolean
  onChange: (patch: Partial<NodeCore>) => void
}) {
  return (
    <>
      <Field label="Type">{(id) => <TypeSelect id={id} value={node.type} disabled={readOnly} onChange={(type) => onChange({ type })} />}</Field>
      <Field label="Status">
        {(id) => <StatusSelect id={id} value={node.status} allowNone disabled={readOnly} onChange={(status) => onChange({ status })} />}
      </Field>
      <Field label="Owner" hint="A person, role, team, service or system.">
        {(id) => <TextInput id={id} value={node.owner} readOnly={readOnly} maxLength={LIMITS.owner} onChange={(event) => onChange({ owner: event.target.value })} />}
      </Field>
      <Field label="Icon">{() => <IconPicker value={node.icon} disabled={readOnly} onPick={(icon) => onChange({ icon })} />}</Field>
      <Field label="Tags">{(id) => <TagInput id={id} value={node.tags} suggestions={labels.tags} disabled={readOnly} onChange={(tags) => onChange({ tags })} />}</Field>
      <PlacementFields value={node.placement} labels={labels} disabled={readOnly} onChange={(placement) => onChange({ placement })} />
      <Field label="References">
        {() => (readOnly && node.references.length === 0 ? <span>None</span> : <ReferencesEditor references={node.references} onChange={(references) => onChange({ references })} />)}
      </Field>
      <Field label="Relationships">
        {() => <RelationshipList relationships={node.relationships} targets={targets} noun="node" readOnly={readOnly} onChange={(relationships) => onChange({ relationships })} />}
      </Field>
    </>
  )
}
